import { type AxiosResponse } from 'axios';
import { query } from './query';
import store, {
   type BulkClosingPayload,
   type BulkClosingResponse,
   type ClosingHubJobStatus,
   type ClosingMatrixImportResponse,
   type CompletionStats,
   type EntityStatusResponse,
   type KpiDetailsData,
   type PeriodsState,
   type ValidationStatusHierarchyResponse,
   type ValidationStatusRecord,
} from './store';
import { periodContextQuery } from 'context/PeriodContext/query';
import CRUDService from 'state/CRUDService';
import UsersService from 'state/User/service';
import {
   type ISO8601DateOnlyString,
   type KpiContentsResponse,
   type Organisation,
   type OrganisationLockStatus,
   type Period,
   type RollForwardConfig,
} from 'types/models';
import { hydrateAllOrganisations } from 'utils/kpiDataAdapter';

export default class PeriodsService extends CRUDService<PeriodsState> {
   private static instance: PeriodsService;
   private usersService!: UsersService;
   private hierarchicalOrganisationsAbortController: AbortController | null = null;
   private kpiDetailsAbortController: AbortController | null = null;
   private entityStatusAbortControllers = new Map<string, AbortController>();
   private validationStatusAbortControllers = new Map<string, AbortController>();

   constructor() {
      if (!PeriodsService.instance) {
         super('periods', store, query);
         this.usersService = new UsersService();
         PeriodsService.instance = this;
      }

      // Service shall be instantiated only once, because otherwise the observable will be created for each service instance

      return PeriodsService.instance;
   }

   async updatePeriodContextIfModified(entityId: Period['id']) {
      const periodId = periodContextQuery.getValue().id;
      if (entityId === periodId) {
         void this.usersService.getLoggedInUser();
      }
   }

   async updateEntity(entityId: Period['id'], changes: Partial<Period>) {
      const updateResult = await super.updateEntity(entityId, changes);
      void this.updatePeriodContextIfModified(entityId);
      return updateResult;
   }

   async rollForward(
      dataToRollforward: RollForwardConfig,
      startPeriod: { from: Date | ISO8601DateOnlyString; to: Date | ISO8601DateOnlyString },
      targetPeriod: { from: Date | ISO8601DateOnlyString; to: Date | ISO8601DateOnlyString }
   ) {
      return this.httpClient
         .post(`/${this.version}/periods/rollforward`, { dataToRollforward, startPeriod, targetPeriod })
         .then((response) => response.data)
         .catch((err) => err);
   }

   async closePeriod(periodId: Period['id'], forceClosing: boolean, cascading: boolean): Promise<{ data: any; status: number }> {
      const queryParams = new URLSearchParams();

      // use from and to instead of periodId
      // queryParams.set('from', periodContext?.activePeriod?.from);
      // queryParams.set('to', periodContext?.activePeriod?.to);
      queryParams.set('forceClosing', String(forceClosing));
      queryParams.set('cascading', String(cascading));

      return this.httpClient
         .post(`/${this.version}/periods/${periodId}/close?${queryParams}`)
         .then((response) => ({ data: response.data, status: response.status }))
         .catch((err) => {
            throw new Error(err);
         });
   }

   async getHierarchicalOrganisations(periodId: number) {
      if (periodContextQuery.getValue().activePeriodId !== periodId) {
         return null;
      }
      if (this.hierarchicalOrganisationsAbortController) {
         this.hierarchicalOrganisationsAbortController.abort();
      }

      this.hierarchicalOrganisationsAbortController = new AbortController();
      const currentAbortController = this.hierarchicalOrganisationsAbortController;

      store.update({ isHierarchicalOrganisationsLoading: true });

      return this.httpClient
         .get(`/${this.version}/periods/${periodId}/organisations`, {
            signal: currentAbortController.signal,
         })
         .then((response) => {
            if (!currentAbortController.signal.aborted && periodContextQuery.getValue().activePeriodId === periodId) {
               store.update({
                  hierarchicalOrganisations: response.data,
                  isHierarchicalOrganisationsLoading: false,
               });
            }
            return response.data;
         })
         .catch((err) => {
            const isAbortError = err.name === 'AbortError' || err.name === 'CanceledError' || err.code === 'ERR_CANCELED';

            if (isAbortError || currentAbortController.signal.aborted) {
               return null;
            }

            store.update({ isHierarchicalOrganisationsLoading: false });
            throw new Error(err);
         });
   }

   async getEntityStatus(periodId: Period['id'], organisationId?: Organisation['id']): Promise<EntityStatusResponse> {
      const requestKey = `${periodId}-${organisationId ?? 'null'}`;

      if (this.entityStatusAbortControllers.has(requestKey)) {
         this.entityStatusAbortControllers.get(requestKey)?.abort();
      }

      const abortController = new AbortController();
      this.entityStatusAbortControllers.set(requestKey, abortController);

      const queryParams = new URLSearchParams();

      if (organisationId) {
         queryParams.set('organisationId', String(organisationId));
      }

      return this.httpClient
         .get(`/${this.version}/periods/${periodId}/entity-status?${queryParams}`, {
            signal: abortController.signal,
         })
         .then((response) => {
            if (!abortController.signal.aborted) {
               const data = response.data;
               const statusRecords = Array.isArray(data) ? data : Array.isArray(data?.statusRecords) ? data.statusRecords : [];
               const pendingJob = Array.isArray(data) ? null : (data?.pendingJob ?? null);

               store.update({ entityStatusRecords: statusRecords });
               this.entityStatusAbortControllers.delete(requestKey);

               return { statusRecords, pendingJob };
            }

            return { statusRecords: [], pendingJob: null };
         })
         .catch((err) => {
            const isAbortError = err.name === 'AbortError' || err.name === 'CanceledError' || err.code === 'ERR_CANCELED';

            if (isAbortError || abortController.signal.aborted) {
               this.entityStatusAbortControllers.delete(requestKey);
               return { statusRecords: [], pendingJob: null };
            } else {
               this.entityStatusAbortControllers.delete(requestKey);
               throw new Error(err);
            }
         });
   }

   async getValidationStatus(
      periodId: Period['id'],
      organisationId?: Organisation['id'],
      includeSubsidiaries = false,
      aggregate = false
   ): Promise<ValidationStatusRecord[]> {
      const requestKey = `${periodId}-${organisationId ?? 'null'}-${includeSubsidiaries}-${aggregate}-validation`;

      if (this.validationStatusAbortControllers.has(requestKey)) {
         this.validationStatusAbortControllers.get(requestKey)?.abort();
      }

      const abortController = new AbortController();
      this.validationStatusAbortControllers.set(requestKey, abortController);

      const queryParams = new URLSearchParams();

      if (organisationId) {
         queryParams.set('organisationId', String(organisationId));
      }

      queryParams.set('includeSubsidiaries', String(includeSubsidiaries));

      if (aggregate) {
         queryParams.set('aggregate', 'true');
      }

      return this.httpClient
         .get(`/${this.version}/periods/${periodId}/validation-status?${queryParams}`, {
            signal: abortController.signal,
         })
         .then((response) => {
            if (!abortController.signal.aborted) {
               const validationRecords = response.data ?? [];
               store.update({ validationStatusRecords: validationRecords });
               this.validationStatusAbortControllers.delete(requestKey);
            }

            return response.data ?? [];
         })
         .catch((err) => {
            const isAbortError = err.name === 'AbortError' || err.name === 'CanceledError' || err.code === 'ERR_CANCELED';

            if (isAbortError || abortController.signal.aborted) {
               this.validationStatusAbortControllers.delete(requestKey);
               return [];
            }

            this.validationStatusAbortControllers.delete(requestKey);
            this.setError(err);
            throw new Error(err);
         });
   }

   async getValidationStatusHierarchy(
      periodId: Period['id'],
      organisationId: Organisation['id'],
      includeSubsidiaries?: boolean
   ): Promise<ValidationStatusHierarchyResponse> {
      const requestKey = `${periodId}-${organisationId}-${includeSubsidiaries ?? false}-hierarchy-validation`;

      if (this.validationStatusAbortControllers.has(requestKey)) {
         this.validationStatusAbortControllers.get(requestKey)?.abort();
      }

      const abortController = new AbortController();
      this.validationStatusAbortControllers.set(requestKey, abortController);

      const params = new URLSearchParams({ organisationId: String(organisationId) });
      if (includeSubsidiaries !== undefined) {
         params.set('includeSubsidiaries', String(includeSubsidiaries));
      }

      return this.httpClient
         .get(`/v2/periods/${periodId}/validation-status-hierarchy?${params.toString()}`, {
            signal: abortController.signal,
         })
         .then((response) => {
            this.validationStatusAbortControllers.delete(requestKey);
            return response.data;
         })
         .catch((err) => {
            const isAbortError = err.name === 'AbortError' || err.name === 'CanceledError' || err.code === 'ERR_CANCELED';

            if (isAbortError || abortController.signal.aborted) {
               this.validationStatusAbortControllers.delete(requestKey);
               return {
                  organisationId,
                  overall: {
                     worstStatus: 'default' as const,
                     validationRuleStrict: 0,
                     validationRuleSoft: 0,
                     validationRuleNoValue: 0,
                     validationRulesInRange: 0,
                  },
                  kpiAreas: {},
                  kpis: {},
                  kpiSubs: {},
               };
            }

            this.validationStatusAbortControllers.delete(requestKey);
            this.setError(err);
            throw new Error(err);
         });
   }

   async disableEntity(
      periodId: Period['id'],
      entityType: string,
      entityId: string,
      cascade = true,
      organisationId?: string,
      reportingStandardId?: number
   ): Promise<{ data: any; status: number }> {
      return this.httpClient
         .post(`/${this.version}/periods/${periodId}/disable`, { entityType, entityId, cascade, organisationId, reportingStandardId })
         .then((response) => ({ data: response.data, status: response.status }))
         .catch((err) => {
            throw new Error(err);
         });
   }

   async enableEntity(
      periodId: Period['id'],
      entityType: string,
      entityId: string,
      cascade = true,
      organisationId?: string,
      reportingStandardId?: number
   ): Promise<{ data: any; status: number }> {
      return this.httpClient
         .post(`/${this.version}/periods/${periodId}/enable`, { entityType, entityId, cascade, organisationId, reportingStandardId })
         .then((response) => ({ data: response.data, status: response.status }))
         .catch((err) => {
            throw new Error(err);
         });
   }

   async bulkSetEntityStatus(periodId: Period['id'], payload: BulkClosingPayload): Promise<{ data: BulkClosingResponse; status: number }> {
      return this.httpClient
         .post(`/${this.version}/periods/${periodId}/entity-status/bulk`, payload)
         .then((response) => ({ data: response.data as BulkClosingResponse, status: response.status }))
         .catch((err) => {
            throw new Error(err);
         });
   }

   async exportClosingMatrix(periodId: Period['id'], reportingStandardId?: number): Promise<AxiosResponse<Blob>> {
      const params = new URLSearchParams();
      if (reportingStandardId) {
         params.set('reportingStandardId', String(reportingStandardId));
      }
      const query = params.size > 0 ? `?${params.toString()}` : '';

      return this.httpClient.get(`/${this.version}/periods/${periodId}/entity-status/matrix/export${query}`, { responseType: 'blob' });
   }

   async importClosingMatrix(periodId: Period['id'], file: File, dryRun: boolean): Promise<{ data: ClosingMatrixImportResponse; status: number }> {
      const form = new FormData();
      form.append('file', file);

      return this.httpClient
         .post(`/${this.version}/periods/${periodId}/entity-status/matrix/import?dryRun=${String(dryRun)}`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
         })
         .then((response) => ({ data: response.data as ClosingMatrixImportResponse, status: response.status }));
   }

   async disableParentOrganization(periodId: Period['id'], organisationId: Organisation['id'], cascade = true, reportingStandardId?: number) {
      return this.disableEntity(periodId, 'organisation', String(organisationId), cascade, String(organisationId), reportingStandardId);
   }

   async enableParentOrganization(periodId: Period['id'], organisationId: Organisation['id'], cascade = true, reportingStandardId?: number) {
      return this.enableEntity(periodId, 'organisation', String(organisationId), cascade, String(organisationId), reportingStandardId);
   }

   async disableKpi(periodId: Period['id'], kpiId: string, cascade = true, organisationId?: string, reportingStandardId?: number) {
      return this.disableEntity(periodId, 'kpi', kpiId, cascade, organisationId, reportingStandardId);
   }

   async enableKpi(periodId: Period['id'], kpiId: string, cascade = true, organisationId?: string, reportingStandardId?: number) {
      return this.enableEntity(periodId, 'kpi', kpiId, cascade, organisationId, reportingStandardId);
   }

   async disableKpiSub(periodId: Period['id'], kpiSubId: string, cascade = true, organisationId?: string, reportingStandardId?: number) {
      return this.disableEntity(periodId, 'kpiSub', kpiSubId, cascade, organisationId, reportingStandardId);
   }

   async enableKpiSub(periodId: Period['id'], kpiSubId: string, cascade = true, organisationId?: string, reportingStandardId?: number) {
      return this.enableEntity(periodId, 'kpiSub', kpiSubId, cascade, organisationId, reportingStandardId);
   }

   async disableKpiArea(periodId: Period['id'], kpiAreaId: string, cascade = true, organisationId?: string, reportingStandardId?: number) {
      return this.disableEntity(periodId, 'kpiArea', kpiAreaId, cascade, organisationId, reportingStandardId);
   }

   async enableKpiArea(periodId: Period['id'], kpiAreaId: string, cascade = true, organisationId?: string, reportingStandardId?: number) {
      return this.enableEntity(periodId, 'kpiArea', kpiAreaId, cascade, organisationId, reportingStandardId);
   }

   async getClosingHubJobStatus(periodId: Period['id'], jobId: number): Promise<ClosingHubJobStatus> {
      const response = await this.httpClient.get(`/${this.version}/periods/${periodId}/closing-hub-job/${jobId}`);
      return response.data;
   }

   async pollClosingHubJob(
      periodId: Period['id'],
      jobId: number,
      onProgress?: (status: string) => void,
      maxAttempts = 900 // 30 minutes at 2s intervals
   ): Promise<ClosingHubJobStatus> {
      const pollInterval = 2000; // 2 seconds
      let attempts = 0;

      return new Promise((resolve, reject) => {
         const poll = async () => {
            try {
               attempts++;
               if (attempts > maxAttempts) {
                  reject(new Error('Job polling timeout exceeded'));
                  return;
               }

               const job = await this.getClosingHubJobStatus(periodId, jobId);
               onProgress?.(job.status);

               switch (job.status) {
                  case 'SUCCESS':
                     resolve(job);
                     return;
                  case 'ERROR':
                  case 'CANCELLED':
                     reject(new Error(job.error ?? `Job ${job.status.toLowerCase()}`));
                     return;
                  case 'PENDING':
                     setTimeout(() => void poll(), pollInterval);
                     break;
               }
            } catch (error) {
               reject(error instanceof Error ? error : new Error(String(error)));
            }
         };
         void poll();
      });
   }

   async getCompletionStats(periodId: Period['id'], organisationId?: Organisation['id']): Promise<CompletionStats> {
      store.update({ isCompletionStatsLoading: true });

      const queryParams = new URLSearchParams();

      if (organisationId) {
         queryParams.set('organisationId', String(organisationId));
      }

      return this.httpClient
         .get(`/${this.version}/periods/${periodId}/completion-stats?${queryParams}`)
         .then((response) => {
            store.update({
               completionStats: response.data,
               isCompletionStatsLoading: false,
            });
            return response.data;
         })
         .catch((err) => {
            store.update({ isCompletionStatsLoading: false });
            this.setError(err);
            throw err;
         });
   }

   async getKpiDetails(periodId: Period['id'], includeSubsidiaries = false): Promise<KpiDetailsData | null> {
      if (this.kpiDetailsAbortController) {
         this.kpiDetailsAbortController.abort();
      }

      this.kpiDetailsAbortController = new AbortController();
      const currentAbortController = this.kpiDetailsAbortController;

      store.update({ isLoadingKpiDetails: true });

      const queryParams = new URLSearchParams();
      queryParams.set('includeSubsidiaries', String(includeSubsidiaries));

      return this.httpClient
         .get(`/${this.version}/periods/${periodId}/kpi-details?${queryParams}`, {
            signal: currentAbortController.signal,
         })
         .then((response) => {
            if (!currentAbortController.signal.aborted) {
               const isNormalized = response.data && 'kpiDefinitions' in response.data;

               if (isNormalized) {
                  const { kpiDefinitions, organisations, ...rest } = response.data;
                  const hydratedOrganisations = hydrateAllOrganisations(organisations, kpiDefinitions);

                  store.update({
                     kpiDetailsData: {
                        ...rest,
                        organisations: hydratedOrganisations,
                     },
                     kpiDefinitions: kpiDefinitions || null,
                     isLoadingKpiDetails: false,
                  });
               } else {
                  store.update({
                     kpiDetailsData: response.data,
                     kpiDefinitions: null,
                     isLoadingKpiDetails: false,
                  });
               }
            }
            return response.data;
         })
         .catch((err) => {
            const isAbortError = err.name === 'AbortError' || err.name === 'CanceledError' || err.code === 'ERR_CANCELED';

            if (isAbortError || currentAbortController.signal.aborted) {
               if (!this.kpiDetailsAbortController || this.kpiDetailsAbortController === currentAbortController) {
                  store.update({ isLoadingKpiDetails: false });
               }
               return null;
            }

            store.update({ isLoadingKpiDetails: false });
            this.setError(err);
            throw err;
         });
   }

   async getKpiContents(periodId: Period['id'], organisationId: Organisation['id'], kpiSubId: number): Promise<KpiContentsResponse> {
      try {
         const response = await this.httpClient.get(
            `/${this.version}/periods/${periodId}/organisation/${organisationId}/kpi-contents?kpiSubId=${kpiSubId}`
         );

         return response.data;
      } catch (error: any) {
         this.setError(error);
         console.error('Get KPI Contents Error:', error);
         throw error;
      }
   }

   /**
    * Get KPI field sums for consolidated mode validation.
    * Uses the /kpisubs/{id}/fields/sums endpoint which returns validation rules
    * with calculated referenceValueSum for consolidated data.
    */
   async getKpiFieldsSums(
      kpiSubId: number,
      organisationId: Organisation['id'],
      from: string,
      to: string,
      includeSubsidiaries: boolean
   ): Promise<KpiContentsResponse> {
      try {
         const queryParams = new URLSearchParams();
         queryParams.set('organisationId', String(organisationId));
         queryParams.set('from', from);
         queryParams.set('to', to);
         queryParams.set('includeSubsidiaries', String(includeSubsidiaries));

         const response = await this.httpClient.get(`/${this.version}/kpisubs/${kpiSubId}/fields/sums?${queryParams.toString()}`);

         const kpiFields = Array.isArray(response.data) ? response.data : (response.data?.kpiFields ?? []);

         return { kpiFields };
      } catch (error: any) {
         this.setError(error);
         console.error('Get KPI Fields Sums Error:', error);
         throw error;
      }
   }

   async exportValidationOverview(periodId: Period['id'], includeSubsidiaries = false): Promise<AxiosResponse<Blob>> {
      const currentParams = new URLSearchParams(this.queryParams);
      currentParams.set('includeSubsidiaries', String(includeSubsidiaries));
      this.queryParams = `?${currentParams.toString()}`;

      try {
         const response = await this.httpClient.get(`/${this.version}/periods/${periodId}/validation-overview/export${this.queryParams}`, {
            responseType: 'blob',
         });

         return response;
      } catch (error: any) {
         this.setError(error);
         throw error;
      }
   }

   async exportKpiFieldsValidationRules(periodId: Period['id'], includeSubsidiaries = false): Promise<AxiosResponse<Blob>> {
      const currentParams = new URLSearchParams(this.queryParams);
      currentParams.set('includeSubsidiaries', String(includeSubsidiaries));
      this.queryParams = `?${currentParams.toString()}`;

      try {
         const response = await this.httpClient.get(`/${this.version}/periods/${periodId}/kpi-fields-validation-rules/export${this.queryParams}`, {
            responseType: 'blob',
         });

         return response;
      } catch (error: any) {
         this.setError(error);
         throw error;
      }
   }

   /**
    * Marks validation items as resolved for a specific organisation and KPI hierarchy level.
    * Updates the store with the resolved status returned from the backend.
    */
   async resolveValidation(
      periodId: Period['id'],
      data: {
         organisationId: number;
         kpiId?: number;
         kpiSubId?: number;
         kpiFieldId?: number;
      }
   ): Promise<{ success: boolean; message: string; items: Array<{ type: string; id: number; isResolved: boolean }> }> {
      try {
         const response = await this.httpClient.post(`/${this.version}/periods/${periodId}/kpi-resolution/resolve`, data);

         // Update the store using backend response
         if (response.data?.items) {
            this.updateResolvedStatus(data.organisationId, response.data.items);
         }

         return response.data;
      } catch (error: any) {
         this.setError(error);
         console.error('Resolve Validation Error:', error);
         throw error;
      }
   }

   /**
    * Marks validation items as unresolved for a specific organisation and KPI hierarchy level.
    * Updates the store with the unresolved status returned from the backend.
    */
   async unresolveValidation(
      periodId: Period['id'],
      data: {
         organisationId: number;
         kpiId?: number;
         kpiSubId?: number;
         kpiFieldId?: number;
      }
   ): Promise<{ success: boolean; message: string; items: Array<{ type: string; id: number; isResolved: boolean }> }> {
      try {
         const response = await this.httpClient.post(`/${this.version}/periods/${periodId}/kpi-resolution/unresolve`, data);

         // Update the store using backend response
         if (response.data?.items) {
            this.updateResolvedStatus(data.organisationId, response.data.items);
         }

         return response.data;
      } catch (error: any) {
         this.setError(error);
         console.error('Unresolve Validation Error:', error);
         throw error;
      }
   }

   private updateResolvedStatus(organisationId: number, items: Array<{ type: string; id: number; isResolved: boolean }>): void {
      const currentState = query.getValue();
      const kpiDetailsData = currentState.kpiDetailsData;

      if (!kpiDetailsData?.organisations || !items || items.length === 0) {
         return;
      }

      const updatedData = JSON.parse(JSON.stringify(kpiDetailsData));

      const updateItem = (org: any, item: { type: string; id: number; isResolved: boolean }): boolean => {
         if (org.id !== organisationId) {
            return false;
         }

         if (!org.kpiAreas) {
            return false;
         }

         for (const kpiArea of org.kpiAreas) {
            if (item.type === 'kpiArea' && kpiArea.id === item.id) {
               kpiArea.isResolved = item.isResolved;
               return true;
            }

            if (kpiArea.kpis) {
               for (const kpi of kpiArea.kpis) {
                  if (item.type === 'kpi' && kpi.id === item.id) {
                     kpi.isResolved = item.isResolved;
                     return true;
                  }

                  if (kpi.kpiSubs) {
                     for (const kpiSub of kpi.kpiSubs) {
                        if (item.type === 'kpiSub' && kpiSub.id === item.id) {
                           kpiSub.isResolved = item.isResolved;
                           return true;
                        }

                        if (item.type === 'kpiField' && kpiSub.kpiFields) {
                           for (const kpiField of kpiSub.kpiFields) {
                              if (kpiField.id === item.id) {
                                 kpiField.isResolved = item.isResolved;
                                 return true;
                              }
                           }
                        }
                     }
                  }
               }
            }
         }

         return false;
      };

      const traverseOrganisation = (org: any, item: { type: string; id: number; isResolved: boolean }): boolean => {
         if (updateItem(org, item)) {
            return true;
         }

         if (org.subsidiaries) {
            for (const subsidiary of org.subsidiaries) {
               if (traverseOrganisation(subsidiary, item)) {
                  return true;
               }
            }
         }

         return false;
      };

      for (const item of items) {
         for (const org of updatedData.organisations) {
            if (traverseOrganisation(org, item)) {
               break;
            }
         }
      }

      store.update({ kpiDetailsData: updatedData });
   }

   async getOrganisationLockStatus(periodId: Period['id']): Promise<OrganisationLockStatus[]> {
      return this.httpClient
         .get(`/${this.version}/periods/${periodId}/organisation-lock-status`)
         .then((response) => response.data)
         .catch(() => {
            return [];
         });
   }

   cancelAllPendingRequests(): void {
      if (this.hierarchicalOrganisationsAbortController) {
         this.hierarchicalOrganisationsAbortController.abort();
         this.hierarchicalOrganisationsAbortController = null;
      }

      if (this.kpiDetailsAbortController) {
         this.kpiDetailsAbortController.abort();
         this.kpiDetailsAbortController = null;
      }

      this.entityStatusAbortControllers.forEach((controller) => {
         controller.abort();
      });
      this.entityStatusAbortControllers.clear();

      this.validationStatusAbortControllers.forEach((controller) => {
         controller.abort();
      });
      this.validationStatusAbortControllers.clear();
   }
}
